import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Search, Menu, X, Phone } from "lucide-react";
import Marquee from "react-fast-marquee";
import { api } from "@/lib/api";

const CATS = [
  { slug: "", name: "హోమ్", to: "/" },
  { slug: "politics", name: "రాజకీయం", to: "/category/politics" },
  { slug: "district", name: "జిల్లా వార్తలు", to: "/category/district" },
  { slug: "agriculture", name: "వ్యవసాయం", to: "/category/agriculture" },
  { slug: "crime", name: "క్రైమ్", to: "/category/crime" },
  { slug: "cinema", name: "సినిమా/వినోదం", to: "/category/cinema" },
  { slug: "special", name: "ప్రత్యేక కథనాలు", to: "/category/special" },
  { slug: "videos", name: "వీడియోలు", to: "/videos" },
  { slug: "contact", name: "సంప్రదించండి", to: "/contact" },
];

export default function Header() {
  const [breaking, setBreaking] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/articles", { params: { breaking: true, limit: 10 } })
      .then((r) => setBreaking(r.data || []))
      .catch(() => setBreaking([]));
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
    setSearchOpen(false);
  };

  const today = new Date().toLocaleDateString("te-IN", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#FDFCF8]/85 border-b border-[#E5E4DC]" data-testid="site-header">
      {/* Top strip */}
      <div className="border-b border-[#E5E4DC] bg-[#2E7D32] text-white text-xs">
        <div className="max-w-7xl mx-auto px-4 py-1.5 flex items-center justify-between">
          <span className="font-body-te" data-testid="header-date">{today}</span>
          <span className="hidden sm:flex items-center gap-2 font-body-te">
            <Phone className="w-3 h-3" /> 9010226164
          </span>
        </div>
      </div>

      {/* Logo row */}
      <div className="max-w-7xl mx-auto px-4 py-5 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
          <div className="w-14 h-14 bg-[#2E7D32] text-white flex items-center justify-center font-heading-te text-2xl border-2 border-[#1B5E20]" aria-hidden>
            న
          </div>
          <div className="leading-tight">
            <div className="font-heading-te text-2xl sm:text-3xl md:text-4xl font-bold text-[#1A1A1A]">
              నల్లమల వాయిస్
            </div>
            <div className="font-body-te text-xs sm:text-sm text-[#4A4A4A] italic mt-0.5">
              ప్రకృతి నేర్పిన బాట... సమాజ మేలే మన మాట
            </div>
          </div>
        </Link>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setSearchOpen((s) => !s)}
            className="p-2 hover:text-[#2E7D32] nv-link"
            aria-label="Search"
            data-testid="search-toggle-btn"
          >
            <Search className="w-5 h-5" />
          </button>
          <button
            className="lg:hidden p-2"
            onClick={() => setMenuOpen((s) => !s)}
            aria-label="Menu"
            data-testid="mobile-menu-btn"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {searchOpen && (
        <form onSubmit={submitSearch} className="border-t border-[#E5E4DC] bg-white" data-testid="search-form">
          <div className="max-w-7xl mx-auto px-4 py-3 flex gap-2">
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="వార్తలను వెతకండి..."
              className="flex-1 border border-[#E5E4DC] px-3 py-2 font-body-te focus:outline-none focus:border-[#2E7D32] nv-selectable"
              data-testid="search-input"
            />
            <button type="submit" className="bg-[#2E7D32] text-white px-4 py-2 font-body-te" data-testid="search-submit-btn">
              వెతుకు
            </button>
          </div>
        </form>
      )}

      {/* Category nav */}
      <nav className={`border-t border-[#E5E4DC] ${menuOpen ? "block" : "hidden"} lg:block`} data-testid="category-nav">
        <div className="max-w-7xl mx-auto px-4">
          <ul className="flex flex-col lg:flex-row lg:items-center gap-1 lg:gap-6 py-2">
            {CATS.map((c) => (
              <li key={c.to}>
                <NavLink
                  to={c.to}
                  end={c.to === "/"}
                  onClick={() => setMenuOpen(false)}
                  data-testid={`nav-${c.slug || "home"}`}
                  className={({ isActive }) =>
                    `block px-2 py-2 font-body-te text-sm lg:text-[15px] font-semibold nv-link ${
                      isActive ? "text-[#2E7D32] border-b-2 border-[#2E7D32]" : "text-[#1A1A1A]"
                    }`
                  }
                >
                  {c.name}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Breaking news */}
      {breaking.length > 0 && (
        <div className="flex items-stretch border-t border-[#E5E4DC] bg-white" data-testid="breaking-ticker">
          <div className="nv-ticker-label text-xs sm:text-sm flex items-center pr-6 font-body-te">
            బ్రేకింగ్ న్యూస్
          </div>
          <Marquee pauseOnHover speed={50} gradient={false} className="flex-1 py-2">
            {breaking.map((a) => (
              <Link
                key={a.id}
                to={`/article/${a.slug}`}
                className="mx-8 font-body-te text-[15px] nv-link"
                data-testid={`breaking-item-${a.slug}`}
              >
                • {a.title}
              </Link>
            ))}
          </Marquee>
        </div>
      )}
    </header>
  );
}
